/**
 * Payout / pricing types for the Request flow gallery screens.
 *
 * Ported from raas-widgets-flow-request (`interfaces/payout.ts`,
 * `api/pricing.ts`, `api/fund-request.ts`) and trimmed to the shapes the
 * gallery screens actually consume. In the gallery these are fed by static
 * mock data instead of the backend pricing/fund-request endpoints.
 */

/** Ways a requester can choose to RECEIVE the money once the request is paid. */
export enum PayoutMethodType {
  BANK_ACCOUNT = "BANK_ACCOUNT",
  CASH_PICKUP = "CASH_PICKUP",
  PUSH_TO_CARD = "PUSH_TO_CARD",
  MOBILE_WALLET = "MOBILE_WALLET",
}

export interface IPayoutMethod {
  type: PayoutMethodType;
  /** Optional payer / network name (e.g. a specific bank or cash network). */
  payer?: string;
}

/** Structured name — mirrors backend `CanonicalName` / flow-send parse. */
export interface CanonicalName {
  firstName: string;
  lastName: string;
  middleName?: string;
  /** Maternal surname (LatAm second last name). */
  secondLastName?: string;
}

/** Smart-native payout details, flattened for the wire. */
export interface FundRequestPayoutInput {
  /** `saved` = recipient-book / destination-cards replay; `new` = fresh input. */
  source?: "new" | "saved";
  // BANK_ACCOUNT
  bankId?: string;
  accountNumber?: string;
  // CASH_PICKUP / HOME_DELIVERY
  stateCode?: string;
  payerId?: string;
  // PUSH_TO_CARD
  cardPan?: string;
  cardExpiry?: string;
  cardholderName?: string;
  paymentMethodId?: string;
  brand?: string;
  last4?: string;
  // MOBILE_WALLET
  carrierId?: string;
  phone?: string;
  // HOME_DELIVERY
  address?: string;
}

/** Send flow payout input (same wire shape as fund-request). */
export type PayoutInput = FundRequestPayoutInput;

/**
 * Recipient identity the sender captures. `canonicalName` is normalized via
 * positional fallback in the gallery (production uses parse/name); `fullName`
 * drives UI labels.
 */
export interface RecipientInput {
  fullName: string;
  canonicalName: CanonicalName | null;
  phone?: string;
  /** Optional OFAC disambiguator (YYYY-MM-DD). Never required. */
  dateOfBirth?: string;
  /**
   * Recipient-book key when the user picked a recent entry. Used to highlight
   * exactly one row in the recent list (fullName alone can collide).
   */
  recipientKey?: string;
  /** Legacy fields — prefer `canonicalName` / `fullName` when present. */
  firstName?: string;
  lastName?: string;
}

export type FundRequestPayoutMethod =
  | "BANK_ACCOUNT"
  | "CASH_PICKUP"
  | "PUSH_TO_CARD"
  | "MOBILE_WALLET"
  | "HOME_DELIVERY";

export interface GenerateFundRequestLinkResult {
  /** Shareable fund-request URL handed to the payer. */
  url: string;
  linkId: string;
  expiresAt: string;
  requestId: string;
}

/** A single priced pre-quote option (public widget projection). */
export interface EstimateOption {
  capabilityKey: string;
  corridorCode: string;
  payerCode: string | null;
  payoutMethod: string;
  fundingMethod: string;
  sendAmount: number;
  receiveAmount: number;
  customerFee: number;
  customerFxRate: number;
  sourceCurrency: string;
  destinationCurrency: string;
}

export interface EstimateRange {
  min: number;
  max: number;
  avg: number;
}

export interface EstimateSummary {
  count: number;
  best?: EstimateOption;
  worst?: EstimateOption;
  rankingMetric: "receiveAmount" | "sendAmount";
  receiveAmount?: EstimateRange;
  sendAmount?: EstimateRange;
  asOf: string;
  indicative: true;
}

export interface EstimateResult {
  options: EstimateOption[];
  summary: EstimateSummary;
}

/** A money request row shown in the requests hub (HomePage). */
export type RequestStatusCode =
  | "pending"
  | "accepted"
  | "funded"
  | "failed"
  | "expired"
  | "cancelled";
